// ---------------------------------------------------------------------------
// M1 — **A DOOR IS A VIEW *AND* A FILTER, AND FOUR ROSTERS HELD FOUR OPINIONS ABOUT IT.**
//
// >>> HER REPORT, 2026-08-17, WITH HER OWN THREE SCREENSHOTS AS THE ACCEPTANCE TESTS: *"7 money
// questions are in your pile"* lands on the GRANTS screen with nothing there, no questions visible,
// nothing to do; *"Readings that failed"* and *"Outside services"* both dump to IT tickets
// generically. <<<
//
// **THIS IS THE THIRD TIME THIS HOUSE HAS FIXED THE DEAD-DOOR DISEASE AND THE FIRST TIME IT HAS
// FIXED THE CAUSE.** A2 (ITEM 8) repaired the HEALTH TILES and wrote the law as arithmetic over
// *that* roster. It could not see the other three, because there are four separate declarations of
// *where does a tap on an alert land*:
//
//   1. `NEEDS_YOU`     (`src/commandCenter.js`) — a bare `to: <view>`
//   2. `HEALTH_TILES`  (`src/commandCenter.js`) — a bare `to: <view>`, held by `healthTileViolations`
//   3. `PATROL_CHECKS` (`src/healthPatrol.js`)  — **no `to` at all**: ONE shared `PATROL_TO` constant
//                                                 for every check on the line
//   4. `ALERT_CLASSES` (`src/standingAlerts.js`) — a `detail:` key
//
// Four spellings of one idea is four answer keys (R28/R29), **and the one that gets missed is the
// one nobody wrote** — which here is literally true: the patrol rows had no destination of their
// own to get wrong, so all six of them went to the same place.
//
// >>> **MEASURED AGAINST THE LIVE DATABASE BEFORE A LINE WAS WRITTEN (R25/R180), AND HER THREE
// CASES ARE THREE DIFFERENT FAULTS. <<<**
//
//   • **THE MONEY LINE COUNTS ONE TABLE AND OPENS ANOTHER.** `moneyPileCount()` reads
//     `triage_drafts WHERE category='financial' AND status='pending'` — **7 rows, live** — and the
//     line's `to` is `grants`, which draws `access_grants`: **400 rows of gifts and refunds, not one
//     of them one of her seven.** She lands on a full screen with nothing on it that she was sent
//     for, which is worse than an empty one: there is no way to tell it is the wrong screen.
//   • **EVERY PATROL ROW OPENED THE SAME GENERIC LIST**, `ok` rows included — six tappable boxes,
//     one destination, `PATROL_TO = "tickets"`.
//   • **AND TWO OF THOSE SIX CAN BE PROVED TO HAVE NOTHING THERE.** `cache` and `vendors` are
//     `files: false` by their own declared ruling (a cost fact is not a broken thing), so they NEVER
//     file a ticket — the door she tapped opens a list that structurally cannot contain the row she
//     tapped it for. *"Outside services"* is her own second screenshot.
//
// **THE REPAIR IS THE CONSOLIDATION AND NOT THE THREE EDITS.** Repointing `money` at `triage` fixes
// her screenshot and leaves the shape that produced it, so the fourth roster written next year gets
// it wrong again. This module is the ONE declaration; all four rosters read it, and
// `doorViolations` is a BICONDITIONAL over their union, so **an alert added anywhere joins by going
// RED rather than by somebody remembering.**
//
// It imports NOTHING, which is the entry requirement that lets a `.mjs` gate load and RUN it, and
// it is a shared `.js` rather than `src/lib/*.ts` because the SERVER composes the money pile's own
// WHERE clause from the same filter (R28/R29 — see `MONEY_PILE_FILTER` below).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// **THE FILTER IS THE HALF THAT MAKES A DOOR A DOOR, AND IT IS SHARED WITH THE COUNT.**
//
// Her clause is *"opens to the EXACT items it counts"*. A view alone cannot satisfy that: `grants`
// is a perfectly real view and it was still the wrong answer. What a door has to carry is the
// SELECTION — and the only way *the door selects what the count counted* can be a property rather
// than a coincidence is for both to read one declaration.
//
// `MONEY_PILE_FILTER` is that declaration. `moneyPileCount()` builds its WHERE clause from it and
// this roster hands the same object to the Drafts view. **A drift between them is not possible**,
// which is the difference between this and a clause asserting that two predicates agree today.
// ---------------------------------------------------------------------------
export const MONEY_PILE_FILTER = { category: "financial", status: "pending" };

// >>> **THERE IS DELIBERATELY NO SECOND ROSTER OF VIEWS IN THIS FILE.** <<< The obvious shape here
// is a `DOOR_VIEWS` list naming the surfaces a door may open onto — and that is this module's own
// disease one level down: a roster of views beside `VIEW_KEYS` is a fifth declaration of what the
// dashboard has, and it rots the first time a view is renamed. `doorViolations` takes the real
// `viewValid` as a PARAMETER instead, so the answer key is the dashboard's own (R28/R29/R31).

// ---------------------------------------------------------------------------
// **THE ROSTER. EVERY ALERT THIS HOUSE DRAWS, AND WHAT IT OPENS ONTO.**
//
// `items` is the sentence that says what she will find there. It is not decoration: A2's law is
// that a box declares what it counts, and a DOOR has to declare what is behind it or nobody can
// tell a working one from `grants`.
//
// **`door: null` IS AN ANSWER AND NOT AN OMISSION (R18/R17/R160).** It means *this alert has no
// actionable detail*, and her clause 4 is that such an alert does not render as a door. It is
// DECLARED with its reason rather than left absent, because an absent entry reads as one somebody
// forgot and the next window adds a door that opens onto nothing.
// ---------------------------------------------------------------------------
export const ALERT_DOORS = [
  // ---- 1. the Overview's actionable list (`NEEDS_YOU`) --------------------------------------
  {
    key: "held",
    door: { view: "moderation", filter: {} },
    items: "The held and flagged items, with the words in front of you.",
  },
  {
    key: "tickets",
    door: { view: "tickets", filter: {} },
    items: "Today's list, open tickets first.",
  },
  {
    // >>> HER FIRST SCREENSHOT. <<< The count has always read `triage_drafts`; the door read
    // `grants`. It opens the DRAFTS view now, filtered to exactly the rows the count counted —
    // where `approve` and `reject` already exist as declared acts on that view, which is her
    // *"with approve/decline controls wherever they actually live"* read literally: the controls
    // were never missing, the screen was.
    key: "money",
    door: { view: "triage", filter: MONEY_PILE_FILTER },
    items: "The money questions waiting on you, each with approve and decline.",
  },
  // DESK-LAW-1 — **BOTH DESK LINES OPEN THE SAME DOOR, BY ONE DECLARATION EACH AND ON PURPOSE.**
  // *"How many are waiting"* and *"how many have waited too long"* are two facts (R18) but ONE place
  // to act: the "Wrote in" view, which is what members sent and what this house answered. Two
  // different destinations would be two answers to *where a waiting member is dealt with* (R28/R29),
  // and the one on the wrong side of that would be the urgent one.
  {
    key: "deskLate",
    door: { view: "messages", filter: {} },
    items: "What members wrote in, and what this house has answered.",
  },
  {
    key: "desk",
    door: { view: "messages", filter: {} },
    items: "What members wrote in, and what this house has answered.",
  },

  // ---- 2. the health tiles (`HEALTH_TILES`) --------------------------------------------------
  { key: "errors", door: { view: "gates", filter: {} }, items: "The gate log, where a thrown handler is listed." },
  { key: "pushSends", door: { view: "analytics", filter: {} }, items: "What this house handed to a phone." },
  { key: "reviewFlags", door: { view: "review", filter: {} }, items: "The turns the gates flagged for your eye." },
  // VENDOR-CREDIT-1 — the same door `errors` opens, and by the same argument (R28/R29): a turn
  // refused for want of credit IS a row in the gate log, carrying the member, the ask and the
  // vendor's own sentence. A second destination would be a second answer to where a recorded failure
  // is looked at, and this class is the one where being sent to the wrong screen costs the most.
  { key: "credit", door: { view: "gates", filter: {} }, items: "The gate log, where every refused turn is listed with the vendor's own words." },
  {
    key: "generationFailures",
    door: { view: "alerts", filter: { class: "generationFailures" } },
    items: "Each member, the reading they did not get, and the cause.",
  },

  // ---- 3. the nightly patrol's rows (`PATROL_CHECKS`) ----------------------------------------
  //
  // **EACH CHECK NAMES ITS OWN DESTINATION NOW.** `PATROL_TO` is deleted at its declaration
  // (DL-2/DT-2b/R181) rather than left standing beside this roster: a retired constant that still
  // resolves is one a later round reads as the answer.
  {
    key: "failures",
    // The SAME door as the `generationFailures` tile, deliberately and by one declaration. The
    // patrol row and the tile are two surfaces naming ONE condition — they even share her wording,
    // *"Readings that failed"* — so two doors would be two answers to where that condition is
    // handled (R28/R29). >>> THIS IS HER SECOND SCREENSHOT: the tile went to `alerts` and the
    // patrol row beside it went to `tickets`, and the row is the one she tapped. <<<
    door: { view: "alerts", filter: { class: "generationFailures" } },
    items: "Each member, the reading they did not get, and the cause.",
  },
  {
    // >>> **`money` IS THE ONE KEY TWO ROSTERS SHARE, AND THEY MEAN DIFFERENT THINGS.** <<< Found by
    // this round's own fixture, on the clause asking that no key be declared twice. The Overview
    // LINE's `money` is *money questions in her pile* (financial triage drafts she owes somebody an
    // answer on); the PATROL CHECK's `money` is *the takings do not reconcile to Stripe*. They are
    // not the same condition and they cannot share a door — sending her to the Drafts view because
    // Stripe did not add up would be this round's own defect, committed by the round repairing it.
    //
    // So a door may declare the roster it belongs to, and `from` is asked FIRST. Entries without it
    // stay the answer for every roster, which is what keeps the common case a single declaration.
    key: "money",
    from: "patrol",
    door: { view: "financials", filter: {} },
    items: "The takings, lane by lane, read back against Stripe itself.",
  },
  {
    key: "production",
    // **NO DOOR, AND THE REASON IS THAT THERE IS NOTHING IN THIS HOUSE TO OPEN.** This check asks
    // whether the live site answers and which build it is running. A red is a deploy or a host
    // problem: the answer is on Railway, not on a screen here. Filing it on her IT sheet is what
    // this check does (`files: true`) and that is the right carrier for it.
    door: null,
    why: "Whether the site is up is answered on the host, not on a screen in the house.",
  },
  {
    key: "cache",
    // `files: false` by its own existing ruling — a low reuse rate is a cost fact, not a broken
    // thing — so there is provably nothing on the IT sheet for it either.
    door: null,
    why: "How much outside work is reused is a cost fact to read, not a pile to work through.",
  },
  {
    // >>> HER THIRD SCREENSHOT. <<< Same shape as `cache`, and the sharpest case: it is
    // `files: false`, so the tickets list it used to open **could never contain a row about it.**
    key: "vendors",
    door: null,
    why: "How the outside services answered is a reading to look at, not a pile to work through.",
  },
];

export const ALERT_DOOR_KEYS = ALERT_DOORS.map((d) => d.key);

/**
 * The door for an alert key, or `null`.
 *
 * **AN UNROSTERED KEY GETS `null` RATHER THAN A PLAUSIBLE DEFAULT, AND THE DIRECTION IS THE RULING
 * (R19/R8-4).** A default of `tickets` is exactly the state this round is repairing: it renders a
 * box that promises to open onto something and does not. `null` renders no door at all — visible,
 * reportable in one sentence, and one roster entry from fixed.
 */
export function alertDoor(key, from = "") {
  const k = String(key || "");
  if (!k) return null;
  // **THE ROSTER-SPECIFIC ENTRY WINS, AND THE SHARED ONE IS THE FALLBACK.** `money` is the one key
  // two rosters both use for two different conditions (see its entries above), so a caller that
  // knows which roster it is asking from gets the right answer and one that does not still gets the
  // common one rather than nothing.
  const scope = String(from || "");
  const row = (scope && ALERT_DOORS.find((d) => d.key === k && d.from === scope))
    || ALERT_DOORS.find((d) => d.key === k && !d.from);
  if (!row || !row.door) return null;
  const view = String(row.door.view || "");
  if (!view) return null;
  const filter = row.door.filter && typeof row.door.filter === "object" ? row.door.filter : {};
  return { key: k, view, filter, items: String(row.items || "") };
}

/** Is this alert key one the roster knows about at all? Separate from having a DOOR (R18). */
export const alertRostered = (key) => ALERT_DOOR_KEYS.includes(String(key || ""));

/**
 * **HER CLAUSE 4: AN ALERT WITH NO ACTIONABLE DETAIL BEHIND IT DOES NOT RENDER.**
 *
 * Asked of a row rather than of a key, because the same key is an alert on one state and a readout
 * on another. Two facts, and only their conjunction is an alert (R18):
 *
 *   • **is this row saying something is wrong** — an `ok` patrol row is a health readout, not an
 *     alert, and drawing it as a tappable box is what made six rows into six dead doors; and
 *   • **is there somewhere to go** — `alertDoor` above.
 *
 * >>> **THE ONE PLACE HER SENTENCE IS READ NARROWLY, DECLARED RATHER THAN QUIETLY TAKEN
 * (DECISIONS M1-3):** what does not render is the **DOOR**, never the reading. A red `vendors` row
 * still prints its own words on the line, because that sentence IS the answer she would have gone
 * looking for — and hiding a measurement to satisfy a clause about dead doors would lose her
 * information in the one direction she cannot notice (R19). What is removed is the promise that
 * tapping it leads somewhere. <<<
 */
export function alertRendersAsDoor(row, door = undefined) {
  if (!row || typeof row !== "object") return false;
  const state = String(row.state || "");
  // Presence, never truthiness (R53/R136/R137): these arrive off a JSON payload, and `Boolean("ok")`
  // is `true`. Only the literal states this house writes are states.
  if (state && state === "ok") return false;
  const n = row.n;
  // A count of zero is not an alert. `Number(true)` is 1 and `Number("")` is 0, so a value that is
  // not a real number is NOT read as a count at all — it leaves the row a readout rather than
  // inventing a figure to draw a door for.
  if (typeof n === "number" && Number.isFinite(n) && n <= 0) return false;
  // >>> **THE DOOR IS THE ONE THE CALLER RESOLVED, NOT A SECOND LOOKUP, AND THIS ROUND'S OWN
  // MUTATION RUN IS WHY.** <<< The first draft re-derived it here from the key alone. That is two
  // answers to *does this alert have a door* on one row (R28/R29) — and M4 proved it reachable: a
  // caller handed a hard-coded destination kept `door: "tickets"` on every row while this function,
  // asking the roster again, went on answering `false` for `vendors`. **The row would then claim a
  // door and refuse to draw one, or the reverse**, and the whole point of the field is that the
  // surface does not have to decide. `undefined` means *nobody resolved one for me*, so the
  // unscoped lookup is the fallback and no existing caller changes behaviour.
  const resolved = door === undefined ? alertDoor(row.key) : door;
  return Boolean(resolved && resolved.view);
}

/**
 * The door as the query a surface really sends.
 *
 * **AN EMPTY FILTER IS AN EMPTY QUERY AND NEVER `?`**, so a door with no selection is byte-for-byte
 * the navigation that view has always had — the failure mode of this whole mechanism is today's
 * behaviour and never something worse.
 */
export function doorQuery(door) {
  if (!door || typeof door !== "object" || !door.filter) return "";
  const parts = [];
  for (const [k, v] of Object.entries(door.filter)) {
    if (typeof v !== "string" || !v) continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  }
  return parts.join("&");
}

/**
 * **THE BICONDITIONAL, OVER THE UNION OF ALL FOUR ROSTERS.**
 *
 * Every alert any roster draws has an entry here, and every entry here names an alert some roster
 * really draws. **Both directions cost something and they cost different things:** a drawn alert
 * with no entry falls back to `null` and quietly stops being tappable (her clause 4 firing on a
 * healthy alert, which is R19's loud direction but still wrong); an entry naming nothing is a
 * receipt nobody cashes and it rots silently, which is how `PATROL_TO` survived.
 *
 * Every roster is a PARAMETER so a gate drives this over rosters this repo does not contain
 * (R129/R135) rather than measuring its own alphabet.
 */
export function doorViolations({
  needsKeys = [], tileKeys = [], patrolKeys = [], classKeys = [], viewExists = () => true,
  // >>> **THE ROSTER IS A PARAMETER TOO, AND THIS ROUND'S OWN MUTATION RUN IS WHY (M19/M20/M22).**
  // <<< Read only off the module's own `ALERT_DOORS`, three of these clauses could be driven ONLY
  // over entries that already satisfy them — so deleting the clause changed nothing and the fixture
  // stayed green on a real hole. A law checked against the one roster it was written from agrees
  // with itself (R129/R135/R146); driven over entries this repo does not contain, it has teeth.
  doors = ALERT_DOORS,
} = {}) {
  const bad = [];
  const drawn = new Set([...needsKeys, ...tileKeys, ...patrolKeys, ...classKeys].map(String));
  const seen = new Set();
  for (const row of doors) {
    const k = String(row?.key || "");
    if (!k) { bad.push("an alert door has no key"); continue; }
    // The identity of a door is its key AND the roster it is scoped to, because `money` legitimately
    // names two different conditions on two rosters. A key declared twice for the SAME scope is
    // still a collision and is still refused.
    const id = row.from ? `${k}@${row.from}` : k;
    if (seen.has(id)) bad.push(`the ${id} door is declared twice`);
    seen.add(id);
    if (!drawn.has(k)) bad.push(`the ${k} door names an alert no roster draws`);
    if (row.door) {
      const view = String(row.door.view || "");
      if (!view) bad.push(`the ${k} door names no view`);
      else if (!viewExists(view)) bad.push(`the ${k} door opens ${view}, which is not a view on this dashboard`);
      if (!row.items || !String(row.items).trim()) {
        bad.push(`the ${k} door does not say what is behind it`);
      }
      // A filter has to be a flat map of strings, or `doorQuery` drops it silently and the door
      // opens the right view UNFILTERED — which is her first screenshot one step less wrong.
      const filter = row.door.filter;
      if (filter && typeof filter === "object") {
        for (const [fk, fv] of Object.entries(filter)) {
          if (typeof fv !== "string" || !fv) bad.push(`the ${k} door's ${fk} filter is not a value a query can carry`);
        }
      } else if (filter !== undefined) {
        bad.push(`the ${k} door's filter is not a selection`);
      }
    } else {
      // A door declared absent must say WHY, or the next window cannot tell a ruling from an
      // oversight and adds one (R17/R160: no silent caps).
      if (!row.why || !String(row.why).trim()) bad.push(`the ${k} alert has no door and does not say why`);
      if (row.items) bad.push(`the ${k} alert has no door but claims something is behind it`);
    }
  }
  // The reverse direction asks the KEY rather than the scoped id: an alert is covered if any entry
  // answers for it, scoped or not. A drawn alert with nothing at all is what this half is about.
  const keys = new Set(doors.map((d) => String(d.key)));
  for (const k of drawn) if (!keys.has(k)) bad.push(`the ${k} alert is drawn and has no door declared`);
  return bad;
}
